import React, { useEffect, useState } from "react";
import { Navigate } from "react-router-dom";

// Hooks and utils
import useAuth from "hooks/useAuth";
import useOrdersTable from "./hooks/useOrdersTable";
import { getFallbackStateOrders } from "./utils/getFallbackStateOrders";

// functionality helpers

// reusable components
import EmptyStateCard from "@/components/ui/EmptyStateCard";
import { SecureTableShell } from "@/components/ui/SecureTableShell";
import OrderSearchFilterControls from "./components/OrderSearchFIlterControls";
import OrdersAnalyticsSummary from "./components/OrdersAnalyticsSummary";

const Orders = () => {
  // Destruct exports from the main hook
  const {
    allOrders,
    orders: filteredOrders,
    searchTerm,
    updateSearchTerm,
    orderStatusFilter,
    updateOrderStatusFilter,
    salesChannelFilter,
    updateSalesChannelFilter,
    isError,
    isLoading,
    orderColumns,
    renderOrderRow,
    refetch,
  } = useOrdersTable();

  // Get fallback status from the helper
  const fallback = getFallbackStateOrders({
    isLoading,
    allData: allOrders,
    filteredData: filteredOrders,
  });

  // Search UI, debounce search for less reactivity
  const [rawSearchTerm, setRawSearchTerm] = useState(searchTerm);
  useEffect(() => {
    const timeout = setTimeout(() => {
      updateSearchTerm(rawSearchTerm);
    }, 600);
    return () => clearTimeout(timeout);
  }, [rawSearchTerm, updateSearchTerm]);

  // TODO: Add and expound more on the hardcoded filters based on available columns
  // Hard coded Filter UI
  const [filters, setFilters] = useState({
    orderStatus: orderStatusFilter,
    paymentStatus: "All",
    salesChannel: salesChannelFilter,
  });
  useEffect(() => {
    updateOrderStatusFilter(filters.orderStatus);
    updateSalesChannelFilter(filters.salesChannel);
    // TODO: PaymentStatus is not yet handled in hook. Handle it later
  }, [filters, updateOrderStatusFilter, updateSalesChannelFilter]);

  // Check if user is authenticated
  const { isAuthenticated } = useAuth();
  if (!isAuthenticated) {
    return <Navigate to="/auth/sign-in" replace />;
  }

  // Error fallback
  // TODO: Improve the messaging and logging of the errors.
  if (isError) {
    return (
      <div className="p-6">
        <EmptyStateCard
          icon={
            <div className="w-16 h-16 flex items-center justify-center bg-red-100 rounded-full">
              <span className="text-red-600 font-bold text-lg">!</span>
            </div>
          }
          title="Something went wrong"
          description="We couldn't load your orders. Please try again later."
          buttonLabel="Retry"
          onAction={refetch}
        />
      </div>
    );
  }

  return (
    <div className="pt-[2em]">
      {/* TODO:Finish up the functionalities that arent working */}
      <OrdersAnalyticsSummary allOrders={allOrders} />

      {/* search and filter  controls */}
      <OrderSearchFilterControls
        searchTerm={rawSearchTerm}
        updateSearchTerm={setRawSearchTerm}
        filters={filters}
        updateFilters={setFilters}
        clearFilters={() =>
          setFilters({
            orderStatus: [],
            paymentStatus: [],
            salesChannel: [],
          })
        }
      />

      {/* Orders table */}
      <SecureTableShell
        data={filteredOrders}
        allData={allOrders}
        columns={orderColumns}
        renderRow={renderOrderRow}
        fallbackState={fallback.state}
      />
    </div>
  );
};

export default Orders;
